import Title from 'antd/es/typography/Title';
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer, LabelList, LineChart, Line } from 'recharts';

const AppGraph = ({ data, isDownloadPdf, totalDownloads, showCompDelay = true }) => {
	const FILL_COLOR = "rgb(59, 130, 246)";
	const androidColor = '#493D9E';
	const iosColor = '#4bbf77';

	// Format the data to match the structure required by the BarChart
	const formattedData = data.map(([monthYear, android, ios]) => ({
		month_year: monthYear,
		android: parseFloat(android).toFixed(2),
		ios: parseFloat(ios).toFixed(2),
		sumValue: (parseFloat(android) + parseFloat(ios)).toFixed(2), // Limit to 2 decimals
	}));

	const formattedRatings = data.map(([monthYear, android, ios, andRatings, iosRatings]) => ({
		month_year: monthYear,
		android: parseFloat(andRatings).toFixed(2),
		ios: parseFloat(iosRatings).toFixed(2),
		sumValue: ((parseFloat(andRatings) + parseFloat(iosRatings)) / 2).toFixed(2), // Limit to 2 decimals
	}));

	const maxValue = formattedData.length ? Math.max(...formattedData.map((item) => Number(item.sumValue) || 0)) : 0;
	const yAxisMaxValue = Math.max(maxValue * 1.1, 1);

	// Custom tooltip to show sum of android and ios with 2 decimal places
	const customTooltip = ({ payload, label }) => {
		if (payload && payload.length) {
			const androidValue = payload[0]?.value
			const iosValue = payload[1]?.value
			const sumValue = payload[2]?.value

			return (
				<div className="custom-tooltip" style={{
					backgroundColor: 'white', padding: '10px', fontSize: "14px",
					borderRadius: '15px', border: '1px solid #ccc'
				}}>
					<p style={{ color: '#000', fontWeight: "bold" }}>{label}</p>
					<p style={{ color: iosColor }}>IOS: {iosValue}M</p>
					<p style={{ color: androidColor }}>Android: {androidValue}M</p>
					<p style={{ color: '#000', fontWeight: 500 }}>Total: {sumValue}M</p>
				</div>
			);
		}
		return null;
	};

	// Custom tooltip to show sum of android and ios with 2 decimal places
	const customTooltipApp = ({ payload, label, coordinate }) => {
		if (payload && payload.length) {
			const androidValue = payload[0]?.payload?.android
			const iosValue = payload[0]?.payload?.ios
			const sumValue = payload[0]?.payload?.sumValue

			let { x } = coordinate;

			if (x > 450) {
				x = x - 130
			}

			return (
				<div className="custom-tooltip" style={{
					backgroundColor: 'white', padding: '10px', borderRadius: '15px', fontSize: "14px",
					border: '1px solid #ccc', position: "absolute", top: 170, left: x, width: "130px"
				}}>
					<p style={{ color: '#000', fontWeight: "bold" }}>{label}</p>
					<p style={{ color: iosColor }}>IOS: {iosValue}</p>
					<p style={{ color: androidColor }}>Android: {androidValue}</p>
					<p style={{ color: '#000', fontWeight: 500 }}>Total: {sumValue}</p>
				</div>
			);
		}
		return null;
	};

	return (<div>
		<div style={{
			position: "relative", paddingRight: "15px", paddingTop: "10px", height: "fit-content", border: '1px solid #ddd',
			borderRadius: '10px', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
		}}>
			<Title level={5} style={{
				background: FILL_COLOR, borderRadius: "5px 20px", marginLeft: "15px",
				paddingInline: "35px", color: "#FFF", width: "fit-content", transition: "height 0.5s ease",
				marginBottom: 15, height: isDownloadPdf ? 32 : 25, boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
			}}>
				Downloads
			</Title>

			{totalDownloads?.[1] ? <pre style={{ position: "absolute", top: 5, right: 10, fontSize: "13.2px", textAlign: "right" }}>
				{`IOS: ${totalDownloads?.[2]}M\nAndriod: ${totalDownloads?.[1]}M`}
			</pre> : ''}
			{showCompDelay &&
				<ResponsiveContainer width="100%" height={420}>
					<BarChart data={formattedData} isAnimationActive={false}>
						<CartesianGrid strokeDasharray="1 5" />
						<XAxis
							dataKey="month_year"
							tick={{ fontSize: 12 }}
							angle={-45}
							textAnchor="end"
							height={70}
						/>
						<YAxis domain={[0, yAxisMaxValue]} />
						<Legend />
						<Tooltip content={customTooltip} />
						<Bar dataKey="android" stackId="a" fill={androidColor} />
						<Bar dataKey="ios" stackId="a" fill={iosColor}>
							<LabelList style={{ fontSize: 12 }} dataKey="sumValue" position="top" formatter={(v) => `${v}M`} />
						</Bar>
					</BarChart>
				</ResponsiveContainer>}

		</div>

		<div style={{
			position: "relative", paddingRight: "15px", marginTop: "20px", paddingTop: "10px", height: "fit-content",
			border: '1px solid #ddd', borderRadius: '10px', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
		}}>

			<Title level={5} style={{
				background: FILL_COLOR, borderRadius: "5px 20px", marginLeft: "15px",
				paddingInline: "35px", color: "#FFF", width: "fit-content", transition: "height 0.5s ease",
				marginBottom: 15, height: isDownloadPdf ? 32 : 25, boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
			}}>
				Ratings
			</Title>

			{totalDownloads?.[1] ? <pre style={{ position: "absolute", top: 5, right: 10, fontSize: "13.2px", textAlign: "right" }}>
				{`IOS: ${totalDownloads?.[4]}\nAndriod: ${totalDownloads?.[3]}`}
			</pre> : ''}

			<div style={{ overflow: 'hidden' }}>
				<div style={{ marginTop: "-100px", }}>
					{showCompDelay &&

						<ResponsiveContainer width="100%" height={400}>
							<LineChart data={formattedRatings} isAnimationActive={false}>
								<CartesianGrid strokeDasharray="1 5" />
								<XAxis
									dataKey="month_year"
									tick={{ fontSize: 12 }}
									angle={-45}
									textAnchor="end"
									height={70}
								/>
								<Tooltip content={customTooltipApp} />
								<YAxis
									domain={[0, 5]}
									ticks={[0, 1, 2, 3, 4, 5]}
									interval="preserveStartEnd"
								/>
								<Legend />
								<Line type="monotone" dataKey="android" stackId="a" stroke={androidColor} />
								<Line type="monotone" dataKey="ios" stackId="a" stroke={iosColor} />
							</LineChart>
						</ResponsiveContainer>
					}
				</div>
			</div>
		</div>
	</div>);
};

export default AppGraph;
